/** Admin Marketing — content, distribution, scheduling & insights. Mock-first. */

/**
 * Master piece kind (what the work product is).
 * Platform-specific shapes live on `ContentVariant.format`.
 */
export type ContentType =
  | 'article'
  | 'blog_post'
  | 'press_release'
  | 'newsletter'
  | 'video_script'
  | 'landing_page'
  | 'campaign'
  | 'social_package';

export type ContentStatus = 'idea' | 'draft' | 'in_review' | 'approved' | 'scheduled' | 'published' | 'archived';

/**
 * Format for a single destination variant — flexible across platforms.
 * One master piece can spawn an IG story, TikTok short, X thread, press pitch, etc.
 */
export type ContentFormat =
  | 'article'
  | 'blog_post'
  | 'press_release'
  | 'newsletter'
  | 'landing_page'
  | 'video_script'
  | 'pitch'
  | 'social_post'
  | 'carousel'
  | 'reel'
  | 'short'
  | 'story'
  | 'x_post'
  | 'x_thread'
  | 'linkedin_post'
  | 'other';

export type CollaboratorRole = 'author' | 'editor' | 'reviewer' | 'approver' | 'designer' | 'social_manager';

/** Link between a contact person and a Sources account or outlet. */
export interface CollaboratorSourceLink {
  /** SourceAccount.id (`acc_*`) or MediaOutlet.id (`out_*`). */
  sourceId: string;
  /** True when this person holds/controls the platform account (social) or owns the outlet relationship. */
  isAccountHolder: boolean;
}

/**
 * Contact person for specific Sources (accounts / outlets).
 * May be an account holder, a press desk, an influencer manager, etc. —
 * not required to hold the platform account themselves.
 * Optional `role` supports Inflows content workflow (authors, approvers).
 */
export interface Collaborator {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: CollaboratorRole;
  /** Platforms and outlets this person is a contact for. */
  sourceLinks: CollaboratorSourceLink[];
  active: boolean;
  avatarColor: string;
  joinedAt: string;
  notes?: string;
}

/**
 * A single attached media asset. No real upload/storage backs this yet — either
 * `url` points at a real image, or `tone` renders a placeholder gradient block
 * (mirrors the pattern used by the Media section's `mediaTone`/`mediaLabel`).
 */
export interface ContentMedia {
  kind: 'image' | 'video';
  url?: string;
  tone?: string;
  label?: string;
  alt?: string;
}

export const MEDIA_TONE_PRESETS: { tone: string; label: string }[] = [
  { tone: 'bg-gradient-to-br from-amber-300 via-rose-400 to-fuchsia-500', label: 'Sunset' },
  { tone: 'bg-gradient-to-br from-cyan-400 via-teal-500 to-emerald-700', label: 'Coastal' },
  { tone: 'bg-gradient-to-br from-sky-400 to-blue-700', label: 'Sky' },
  { tone: 'bg-gradient-to-br from-fuchsia-600 via-rose-500 to-cyan-400', label: 'Vibrant' },
  { tone: 'bg-gradient-to-br from-slate-700 to-slate-950', label: 'Slate' },
];

/** One platform/outlet-specific version of a master content piece. */
export interface ContentVariant {
  id: string;
  /** Destination SourceAccount or MediaOutlet. */
  sourceId: string;
  format: ContentFormat;
  /** Caption / body for this destination (may differ from master). */
  body: string;
  /** X-style thread segments after `body`. */
  threadParts?: string[];
  firstComment?: string;
  media?: ContentMedia;
}

export interface MarketingContentItem {
  id: string;
  title: string;
  /** Master piece kind — not the per-platform format. */
  contentType: ContentType;
  status: ContentStatus;
  /** Strategy play this piece hangs off — answers "did it work?" */
  playId?: string;
  /** Recurring series this edition belongs to (if drafted from Series). */
  seriesId?: string;
  /** Master / shared body (brief, longform, or seed copy). */
  body: string;
  excerpt: string;
  authorIds: string[];
  collaboratorIds: string[];
  /** Coarse kinds — derived from variants' destinations. */
  channels: ('traditional' | 'social')[];
  /**
   * Planned destinations (derived from variants for filters/lists).
   * Prefer editing `variants` — each source can have its own format + copy.
   */
  sourceIds: string[];
  /** Per-destination format + copy. Multiple platforms ⇒ multiple variants. */
  variants: ContentVariant[];
  tags: string[];
  featuredMedia?: ContentMedia;
  scheduledAt?: string;
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// SocialPlatform / TraditionalMedium / labels / accents / char limits are
// re-exported from ./platforms — one vocabulary shared with Radar.
export type { SocialPlatform, TraditionalMedium } from './platforms';
export {
  PLATFORM_LABEL, PLATFORM_ACCENT, PLATFORM_CHART_COLOR, PLATFORM_CHAR_LIMIT, MEDIUM_LABEL,
  PUBLISHABLE_PLATFORMS, ALL_SOCIAL_PLATFORMS, isPublishable, normalizeSocialPlatform,
} from './platforms';
import type { SocialPlatform, TraditionalMedium } from './platforms';

/**
 * `publishing` is transient and exists so dispatch can be idempotent: the beat
 * tick claims a post with a conditional UPDATE into this state, so a second
 * worker seeing the same row cannot claim it too. It is rarely visible, but a UI
 * that omits it renders a blank badge exactly when something is mid-publish —
 * the one moment someone is watching.
 */
export type DistributionStatus =
  | 'draft'
  | 'pending_approval'
  | 'queued'
  | 'scheduled'
  | 'publishing'
  | 'published'
  | 'failed';

export interface PostNote {
  id: string;
  authorId: string;
  body: string;
  createdAt: string;
}

/** WIP collaboration comment on content or distribution work. */
export type WorkFeedbackTarget = 'content' | 'social_post' | 'traditional_placement';

export type WorkFeedbackStatus = 'open' | 'resolved';

export interface WorkFeedback {
  id: string;
  targetType: WorkFeedbackTarget;
  targetId: string;
  authorId: string;
  body: string;
  /** People tagged to review / check this feedback. */
  taggedIds: string[];
  status: WorkFeedbackStatus;
  createdAt: string;
  resolvedAt?: string;
  resolvedBy?: string;
}

export const WORK_FEEDBACK_STATUS_LABEL: Record<WorkFeedbackStatus, string> = {
  open: 'Open',
  resolved: 'Resolved',
};

export const WORK_FEEDBACK_STATUS_BADGE: Record<WorkFeedbackStatus, string> = {
  open: 'bg-amber-50 text-amber-800',
  resolved: 'bg-emerald-50 text-emerald-700',
};

export const WORK_FEEDBACK_TARGET_LABEL: Record<WorkFeedbackTarget, string> = {
  content: 'Content',
  social_post: 'Social post',
  traditional_placement: 'Traditional placement',
};

export interface SocialPost {
  id: string;
  contentId?: string;
  /** ContentVariant.id when queued from a master piece. */
  variantId?: string;
  /**
   * Denormalized play for queue filters — usually copied from linked content
   * on queue; can also be set on standalone posts.
   */
  playId?: string;
  /** Destination SourceAccount.id from Marketing → Sources. */
  sourceId?: string;
  /** Platform-native format (reel, story, x_thread, …). */
  format?: ContentFormat;
  platform: SocialPlatform;
  handle: string;
  caption: string;
  /** Additional thread segments after `caption` — X/Bluesky-style multi-part posts. */
  threadParts?: string[];
  /** Auto-added as the first reply once the post goes live (e.g. a hashtag dump). */
  firstComment?: string;
  media?: ContentMedia;
  tags: string[];
  notes: PostNote[];
  status: DistributionStatus;
  scheduledAt?: string;
  publishedAt?: string;
  authorId?: string;
  approvedBy?: string;
  approvedAt?: string;
  metrics?: {
    impressions?: number;
    likes?: number;
    shares?: number;
    comments?: number;
    engagementRate?: number;
    /** Mock daily trend for the per-post performance view — no real time-series backend yet. */
    trend?: { date: string; engagementRate: number }[];
  };
}

/** A quick, unstructured post concept — lighter-weight than a full draft. */
export interface PostIdea {
  id: string;
  text: string;
  platform?: SocialPlatform;
  tags: string[];
  createdAt: string;
}

/** A reusable caption starting point. */
export interface PostTemplate {
  id: string;
  name: string;
  body: string;
  platform?: SocialPlatform;
}

export type EngagementStatus = 'unread' | 'replied' | 'archived';

export interface EngagementComment {
  id: string;
  postId?: string;
  platform: SocialPlatform;
  authorHandle: string;
  authorName: string;
  body: string;
  createdAt: string;
  sentiment?: 'positive' | 'neutral' | 'negative';
  status: EngagementStatus;
  reply?: string;
  repliedAt?: string;
  repliedBy?: string;
}

export const ENGAGEMENT_STATUS_LABEL: Record<EngagementStatus, string> = {
  unread: 'Unread',
  replied: 'Replied',
  archived: 'Archived',
};

export const ENGAGEMENT_STATUS_BADGE: Record<EngagementStatus, string> = {
  unread: 'bg-blue-50 text-blue-700',
  replied: 'bg-emerald-50 text-emerald-700',
  archived: 'bg-gray-100 text-gray-500',
};

export interface TraditionalPlacement {
  id: string;
  contentId?: string;
  variantId?: string;
  /** Denormalized play — same rules as SocialPost.playId. */
  playId?: string;
  /** Destination MediaOutlet.id from Marketing → Sources. */
  sourceId?: string;
  format?: ContentFormat;
  outlet: string;
  medium: TraditionalMedium;
  status: DistributionStatus;
  scheduledAt?: string;
  publishedAt?: string;
  authorId?: string;
  url?: string;
}

export interface TrendMetric {
  id: string;
  label: string;
  channel: 'traditional' | 'social' | 'both';
  volume: number;
  /** null = no prior window volume ("New"), distinct from 0% change. */
  changePct: number | null;
  sentiment: 'positive' | 'neutral' | 'negative';
  engagement?: number;
}

export interface KpiMetric {
  id: string;
  label: string;
  value: number;
  previousValue: number;
  unit: 'number' | 'percent' | 'currency';
  series: { date: string; value: number }[];
}

export interface SentimentPoint {
  date: string;
  /** Share of classified items (0–100) when derived from performance/sentiment counts. */
  positive: number;
  neutral: number;
  negative: number;
  unknown?: number;
  classified?: number;
}

/** Per-post metrics the publishing rail returns on `GET /analytics`. */
export interface PostingInsightMetrics {
  likes: number;
  comments: number;
  shares: number;
  saves: number;
  clicks: number;
  views: number;
  follows: number;
  impressions: number;
  reach: number;
}

/**
 * Zernio `source` on GET /v1/analytics — both are still *our* connected
 * accounts. `late` = published through the rail (Studio). `external` = posted
 * natively on a connected profile and synced back. Neither is a competitor feed.
 */
export type PostingInsightSource = 'late' | 'external';

export interface PostingInsightPost {
  id: string;
  platform: SocialPlatform;
  handle: string;
  source: PostingInsightSource;
  caption: string;
  publishedAt: string;
  mediaTone: string;
  mediaLabel: string;
  metrics: PostingInsightMetrics;
  /** Permalink when the vendor provided one. */
  url?: string | null;
  /** Vendor engagement score (connected basis). Prefer over summing client-side. */
  engagement?: number;
  /** Null = synced before the vendor sent media fields; unknown, not text. */
  format?: PostFormat | null;
}

export type PostFormat = 'video' | 'image' | 'carousel' | 'text';

export interface PostingInsightDaily {
  date: string;
  platform: SocialPlatform;
  posts: number;
  /** Absent = the vendor omitted the key. Do not treat as 0 on a chart. */
  likes?: number;
  comments?: number;
  shares?: number;
  saves?: number;
  views?: number;
  impressions?: number;
  reach?: number;
  clicks?: number;
}

export interface PostingInsightFollowers {
  date: string;
  platform: SocialPlatform;
  followers: number;
}

export const CONTENT_TYPE_LABEL: Record<ContentType, string> = {
  article: 'Article',
  blog_post: 'Blog post',
  press_release: 'Press release',
  newsletter: 'Newsletter',
  video_script: 'Video script',
  landing_page: 'Landing page',
  campaign: 'Campaign',
  social_package: 'Social package',
};

export const CONTENT_FORMAT_LABEL: Record<ContentFormat, string> = {
  article: 'Article',
  blog_post: 'Blog post',
  press_release: 'Press release',
  newsletter: 'Newsletter',
  landing_page: 'Landing page',
  video_script: 'Video script',
  pitch: 'Pitch',
  social_post: 'Feed post',
  carousel: 'Carousel',
  reel: 'Reel',
  short: 'Short',
  story: 'Story',
  x_post: 'X post',
  x_thread: 'X thread',
  linkedin_post: 'LinkedIn post',
  other: 'Other',
};

/** Sensible default format when adding a variant for a platform/medium. */
export function defaultFormatForPlatform(
  platform?: SocialPlatform | 'other' | string,
  medium?: string,
): ContentFormat {
  if (medium) return medium === 'online_news' || medium === 'print' ? 'pitch' : 'pitch';
  switch (platform) {
    case 'x':
      return 'x_post';
    case 'linkedin':
      return 'linkedin_post';
    case 'tiktok':
    case 'youtube':
      return 'short';
    case 'instagram':
      return 'reel';
    case 'facebook':
      return 'social_post';
    default:
      return 'social_post';
  }
}

export const SOCIAL_FORMATS: ContentFormat[] = [
  'social_post',
  'carousel',
  'reel',
  'short',
  'story',
  'x_post',
  'x_thread',
  'linkedin_post',
  'video_script',
  'other',
];

export const TRADITIONAL_FORMATS: ContentFormat[] = [
  'pitch',
  'press_release',
  'article',
  'blog_post',
  'newsletter',
  'other',
];

export const CONTENT_STATUS_LABEL: Record<ContentStatus, string> = {
  idea: 'Idea',
  draft: 'Draft',
  in_review: 'In review',
  approved: 'Approved',
  scheduled: 'Scheduled',
  published: 'Published',
  archived: 'Archived',
};

export const CONTENT_STATUS_BADGE: Record<ContentStatus, string> = {
  idea: 'bg-gray-100 text-gray-600',
  draft: 'bg-gray-100 text-gray-700',
  in_review: 'bg-amber-50 text-amber-800',
  approved: 'bg-sky-50 text-sky-700',
  scheduled: 'bg-violet-50 text-violet-700',
  published: 'bg-emerald-50 text-emerald-700',
  archived: 'bg-gray-100 text-gray-500',
};

export const COLLABORATOR_ROLE_LABEL: Record<CollaboratorRole, string> = {
  author: 'Author',
  editor: 'Editor',
  reviewer: 'Reviewer',
  approver: 'Approver',
  designer: 'Designer',
  social_manager: 'Social manager',
};

export const DISTRIBUTION_STATUS_LABEL: Record<DistributionStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Pending approval',
  queued: 'Queued',
  scheduled: 'Scheduled',
  publishing: 'Publishing…',
  published: 'Published',
  failed: 'Failed',
};

export const DISTRIBUTION_STATUS_BADGE: Record<DistributionStatus, string> = {
  draft: 'bg-gray-100 text-gray-600',
  pending_approval: 'bg-orange-50 text-orange-800',
  queued: 'bg-amber-50 text-amber-800',
  scheduled: 'bg-violet-50 text-violet-700',
  // Mid-flight, claimed by a worker. Blue rather than green: it has not landed.
  publishing: 'bg-blue-50 text-blue-700',
  published: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-red-50 text-red-700',
};

/** Where owned/partner content actually ships — distinct from Media coverage sources. */
export type MarketingChannelKind = 'social' | 'traditional';

export type MarketingChannelOwnership =
  | 'owned'
  | 'partner_influencer'
  | 'partner_media'
  | 'news_outlet';

export interface MarketingChannelBase {
  id: string;
  kind: MarketingChannelKind;
  ownership: MarketingChannelOwnership;
  displayName: string;
  region?: string;
  language?: string;
  contactName?: string;
  contactEmail?: string;
  notes?: string;
  tags: string[];
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MarketingSocialChannel extends MarketingChannelBase {
  kind: 'social';
  ownership: 'owned' | 'partner_influencer';
  platform: SocialPlatform;
  handle: string;
  profileUrl?: string;
  followers?: number;
  verified?: boolean;
}

export interface MarketingTraditionalChannel extends MarketingChannelBase {
  kind: 'traditional';
  ownership: 'owned' | 'partner_media' | 'news_outlet';
  medium: TraditionalMedium;
  /** Outlet / station / publication name */
  outletName: string;
  website?: string;
  pitchEmail?: string;
  /** Circulation, unique visitors, or estimated reach */
  reach?: number;
}

export type MarketingChannel = MarketingSocialChannel | MarketingTraditionalChannel;

export const CHANNEL_OWNERSHIP_LABEL: Record<MarketingChannelOwnership, string> = {
  owned: 'Owned',
  partner_influencer: 'Partner influencer',
  partner_media: 'Partner media',
  news_outlet: 'News outlet',
};

export const CHANNEL_OWNERSHIP_BADGE: Record<MarketingChannelOwnership, string> = {
  owned: 'bg-sky-50 text-sky-700',
  partner_influencer: 'bg-fuchsia-50 text-fuchsia-700',
  partner_media: 'bg-violet-50 text-violet-700',
  news_outlet: 'bg-amber-50 text-amber-800',
};

export const CHANNEL_KIND_LABEL: Record<MarketingChannelKind, string> = {
  social: 'Social',
  traditional: 'Traditional',
};
